import { type SessionContext, scoreDelegationActivity } from "./delegationScoring";

/**
 * Algorithmic baseline routing for an activity. Returns the bucket the scoring
 * engine recommends ("Keep" / "Delegate" / "Plan" / "Drop"), or empty string
 * if the activity is unrouted (not-currently-doing or missing required signal
 * fields). Calls scoreDelegationActivity — session bands feed the stage-aware
 * threshold + CEO-irreducible guard via detectStage.
 *
 * Previously duplicated across 6 files (audit/page, page, sessions/page,
 * results/page, api/generate-ai-report, api/webhook-revisit). Each copy was
 * identical modulo a console.error in the catch block. Centralized here so
 * engine signature changes propagate from one site.
 */
export function getBaseResolution(record: any, session: SessionContext): string {
  if (record.currently_doing === false) return "";
  if (!record.enjoyment_rating || !record.revenue_impact || !record.complexity) return "";
  try {
    const goalAlignments = (record.activity_goals || []).map((g: any) => ({
      goalId: g.goal_id,
      alignment: g.alignment_level || g.alignment,
    }));
    const result = scoreDelegationActivity(
      {
        hoursPerMonth: Number(record.delegation_plan?.rova_tracker?.diy_hours_monthly) || 0,
        energyImpact: record.energy_impact || 0,
        important: record.is_important || false,
        urgent: record.is_urgent || false,
        enjoymentAbility: record.enjoyment_rating || "",
        complexity: record.complexity || "",
        revenueImpact: record.revenue_impact || "",
        strategicPillar: record.strategic_pillar || "Non Strategic",
        goalAlignments,
        isMandatory: record.is_mandatory || false,
      },
      session,
    );

    if (result.decision === "DO YOURSELF") return "Keep";
    if (result.decision === "DELEGATE") return "Delegate";
    if (result.decision === "PLAN") return "Plan";
    if (result.decision === "MUST_DELEGATE") return "MustDelegate";
    return "Drop";
  } catch (e) {
    console.error("Scoring error:", e);
    return "";
  }
}

/**
 * Final routing after user overrides. The four force_* booleans and ill_do_it
 * take precedence (user can manually reroute any activity); falls back to the
 * algorithmic baseline when none is set.
 */
export function getFinalResolution(record: any, session: SessionContext): string {
  if (record.ill_do_it) return "Keep";
  if (record.force_delegation) return "Delegate";
  if (record.force_plan) return "Plan";
  if (record.force_drop) return "Drop";
  if (record.force_must_delegate) return "MustDelegate";
  return getBaseResolution(record, session);
}
